/**
 * Types for `ryn-replay-devtools-capture.ts`, split out (PR 3639 review) so
 * adding a new capture option does not grow that file past the workspace's
 * 500-line gate. Re-exported from the implementation file via `export *`, so
 * every existing import of `./ryn-replay-devtools-capture` is unaffected.
 */

export type RynReplayDevtoolsEmit = (
  type: string,
  payload: Record<string, unknown>
) => void;

/**
 * Scrub mode for a captured console argument tree. `'privacy'` (log/info/warn)
 * redacts every string; `'diagnostic'` (console.error, by default) keeps the
 * full tree with SSN-shaped groups masked.
 */
export type ScrubConsoleArgMode = 'privacy' | 'diagnostic';

export interface IRynReplayDevtoolsCaptureOptions {
  emit: RynReplayDevtoolsEmit;
  /** True when request/response bodies may be retained for this URL. */
  isPayloadRecordingAllowed: (url: string) => boolean;
  /**
   * Ingest URL, when known. Those POSTs are not correlated into the Backend
   * tab — they are our own transport, not the user's API call.
   */
  ingestUrl?: string;
  /**
   * Absolute UPS API bases that may receive a `traceparent` header. Same-origin
   * requests are always eligible; third-party hosts are never stamped.
   */
  correlationAllowedApiBaseUrls?: readonly string[];
  consoleRef?: Console;
  fetchRef?: typeof fetch;
  xhrCtor?: typeof XMLHttpRequest;
  /**
   * Scrub mode for `console.error` specifically (PR 3639 review, MEDIUM: an
   * app whose masking policy opts into `maskAllText` renders raw PII in
   * console.error dumps too, e.g. an HttpErrorResponse carrying a DOB or
   * A-Number). Defaults to `'diagnostic'` (the pre-existing behavior) so
   * every app but the one that opts in is unaffected. `'privacy'` redacts
   * every string, same as log/info/warn.
   */
  consoleErrorMode?: ScrubConsoleArgMode;
}

export interface IRynReplayDevtoolsCapture {
  install(): void;
  uninstall(): void;
}
